import type { IntlShape, MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import React from 'react';
import ReactDOM from 'react-dom';

import type {
	ExtensionManifest,
	ExtensionModule,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import { messages as quickInsertMessages } from '@atlaskit/editor-common/quick-insert/messages';
import { DATABASE_MENU_ITEM } from '@atlaskit/editor-common/quick-insert/keys';
import { CREATE_SECTION_RANK } from '@atlaskit/editor-common/quick-insert/rank';
import { ConfluenceAttributionIcon } from '@atlaskit/editor-common/quick-insert/confluence-attribution-icon';
import {
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions/manifest-helpers';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import DatabaseIcon from '@atlaskit/icon/core/database';
import SmartLinkEmbedIcon from '@atlaskit/icon/core/smart-link-embed';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { DATABASE_VIEW } from '@confluence/named-routes/entry-points/routes.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { getTraceIdFromApolloError } from '@confluence/template-utils/entry-points/errorUtils.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { CREATE_DATABASE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { SessionDataContext } from '@confluence/session-data/entry-points/SessionDataContext.ts';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';

import CreateContentInlineMutation, {
	type CreateContentInlineMutation$data,
} from './__generated__/CreateContentInlineMutation.graphql.ts';
import { i18n } from './confluenceDatabasesExtensionI18n.ts';
import InlineEmbedIcon from './icons/InlineEmbedIcon.svg';
import { DatabaseReferenceModal } from './components/DatabaseReferenceModal.tsx';
import { DatabaseValueContainer } from './components/DatabaseValueContainer.tsx';
type CreateDatabaseExtensionProps = {
	pageId: string;
	spaceKey: string;
	spaceId: string;
	intl: IntlShape;
	experienceTracker?: ExperienceTrackerAPI;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	flags?: FlagsStateContainer;
	contentType?: string;
	allowDatabaseQuickInsert?: boolean;
	isEdit?: boolean;
	pageTreeStateUpdater?: PageTreeStateUpdater;
};

type ExpectedError = {
	description: MessageDescriptor;
	title: MessageDescriptor;
} | null;

const isParentPageMovedError = (error: any) => {
	if (error && 'graphQLErrors' in error) {
		return error.graphQLErrors?.some((graphQLError: any) =>
			graphQLError.extensions.data.errors.some((nestedError: any) =>
				nestedError.message.key.includes('The parent space does not match the desired space'),
			),
		);
	}
	return false;
};

const isInvalidParentError = (error: any) => {
	if (error && 'graphQLErrors' in error) {
		return error.graphQLErrors?.some((graphQLError: any) =>
			graphQLError.extensions.data.errors.some((nestedError: any) =>
				nestedError.message.key.includes('Invalid potential parent passed'),
			),
		);
	}
	return false;
};

export const getExpectedError = (
	error: ApolloErrorInterop | Error | null | undefined,
): ExpectedError => {
	if (isParentPageMovedError(error)) {
		return {
			title: i18n.parentPageMovedErrorTitle,
			description: i18n.parentPageMovedErrorDescription,
		};
	} else if (isInvalidParentError(error)) {
		return {
			title: i18n.invalidParentErrorTitle,
			description: i18n.invalidParentErrorDescription,
		};
	} else if (isUnauthorizedError(error)) {
		return {
			title: i18n.permissionRemovedErrorTitle,
			description: i18n.permissionRemovedErrorDescription,
		};
	}
	return null;
};

const fireFlagActionClickedAnalytics = ({
	createAnalyticsEvent,
}: {
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
}) => {
	createAnalyticsEvent?.({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'redirectToDatabase',
			source: 'editorComponent',
			attributes: {
				flow: 'inlineDatabaseCreation',
				contentType: 'database',
				collabService: 'canvas-relay',
			},
		},
	}).fire();
};

export const createDatabaseQuickInsertModule = ({
	pageId,
	spaceKey,
	spaceId,
	experienceTracker,
	createAnalyticsEvent,
	flags,
	pageTreeStateUpdater,
}: {
	pageId: string;
	spaceKey: string;
	spaceId: string;
	experienceTracker?: ExperienceTrackerAPI;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	flags?: FlagsStateContainer;
	pageTreeStateUpdater?: PageTreeStateUpdater;
}) => {
	return async (): Promise<
		| {
				type: string;
				attrs: {
					extensionKey: string;
					extensionType: string;
					parameters: {
						macroParams: {
							url: {
								value: string;
							};
							alwaysShowTitle: {
								value: string;
							};
						};
					};
					url?: undefined;
				};
		  }
		| {
				type: string;
				attrs: {
					url: string;
					extensionKey?: undefined;
					extensionType?: undefined;
					parameters?: undefined;
				};
		  }
		| undefined
	> => {
		try {
			experienceTracker?.start({
				name: CREATE_DATABASE_EXPERIENCE,
				timeout: ExperienceTimeout.CREATE_CONTENT,
				attributes: {
					flow: 'inlinePageCreation',
				},
			});

			const mutationResult = await mutateInterop<CreateContentInlineMutation$data>({
				mutation: CreateContentInlineMutation,
				variables: { createdInContentId: pageId, spaceKey },
			});
			const contentId = mutationResult?.data?.createContentInline?.id ?? '';

			createAnalyticsEvent?.({
				type: 'sendTrackEvent',
				data: {
					actionSubject: 'content',
					action: 'createdByFlow',
					objectId: contentId,
					objectType: 'database',
					containerId: spaceId,
					containerType: 'space',
					source: 'editorComponent',
					attributes: {
						flow: 'inlineDatabaseCreation',
						contentType: 'database',
						collabService: 'canvas-relay',
					},
				},
			}).fire();

			const fullUrl = getFullDatabaseUrl(contentId, spaceKey);

			await flags?.showFlag({
				type: 'success-circle',
				title: <FormattedMessage {...i18n.successFlagTitle} />,
				actions: [
					{
						content: <FormattedMessage {...i18n.successFlagAction} />,
						href: fullUrl,
						target: '_blank',
						onClick: () => fireFlagActionClickedAnalytics({ createAnalyticsEvent }),
					},
				],
				description: <FormattedMessage {...i18n.successFlagDescription} />,
			});

			if (contentId && pageTreeStateUpdater) {
				pageTreeStateUpdater.setForceFetchPageId(contentId);
			}

			experienceTracker?.succeed({
				name: CREATE_DATABASE_EXPERIENCE,
			});

			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			if (fg('platform_native_embeds_rollout_non_maui_experience')) {
				return {
					type: 'extension',
					attrs: {
						extensionKey: `${NATIVE_EMBED_EXTENSION_KEY}:database`,
						extensionType: NATIVE_EMBED_EXTENSION_TYPE,
						parameters: {
							macroParams: {
								url: { value: fullUrl },
								alwaysShowTitle: { value: 'true' },
							},
						},
					},
				};
			}

			return {
				type: 'embedCard',
				attrs: {
					url: fullUrl,
				},
			};
		} catch (error) {
			const cteTraceId = getTraceIdFromApolloError(error);
			const expectedErrorMessage = getExpectedError(error);
			if (expectedErrorMessage) {
				experienceTracker?.succeed({
					name: CREATE_DATABASE_EXPERIENCE,
					attributes: {
						abortReason: 'expectedErrorMessage',
					},
				});
				await flags?.showErrorFlag({
					title: <FormattedMessage {...expectedErrorMessage.title} />,
					description: <FormattedMessage {...expectedErrorMessage.description} />,
				});
			} else {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.DATABASES,
				});
				experienceTracker?.stopOnError({
					name: CREATE_DATABASE_EXPERIENCE,
					error,
					attributes: {
						cteTraceId,
					},
				});
				await flags?.showErrorFlag({
					title: <FormattedMessage {...i18n.defaultErrorFlagTitle} />,
					description: <FormattedMessage {...i18n.defaultErrorFlagDescription} />,
				});
			}
			markErrorAsHandled(error);
		}
	};
};

export const getFullDatabaseUrl = (contentId: string, spaceKey: string): string => {
	const databaseUrl = DATABASE_VIEW.toUrl({
		spaceKey,
		contentId,
	});

	return window.location.origin + databaseUrl;
};

export const referenceDatabaseQuickInsertModule = (intl: IntlShape) => {
	return async (): Promise<
		| {
				type: string;
				attrs: {
					extensionKey: string;
					extensionType: string;
					parameters: {
						macroParams: {
							url: {
								value: {};
							};
							alwaysShowTitle: {
								value: string;
							};
						};
					};
					url?: undefined;
				};
		  }
		| {
				type: string;
				attrs: {
					url: {};
					extensionKey?: undefined;
					extensionType?: undefined;
					parameters?: undefined;
				};
		  }
		| undefined
	> => {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		const el = document.createElement('div');
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		document.body.appendChild(el);

		const sessionData = await getSessionData();

		let result;
		try {
			result = await new Promise((resolve) => {
				/* oxlint-disable-next-line react-js/no-deprecated */ // eslint-disable-next-line react/no-deprecated
				ReactDOM.render(
					<SessionDataContext.Provider value={sessionData}>
						<DatabaseReferenceModal
							onClose={async (result: string | undefined) => {
								resolve(result);
							}}
							intl={intl}
						/>
					</SessionDataContext.Provider>,
					el,
				);
			});
		} finally {
			/* oxlint-disable-next-line react-js/no-deprecated */ // eslint-disable-next-line react/no-deprecated
			ReactDOM.unmountComponentAtNode(el);
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			document.body.removeChild(el);
		}

		if (result) {
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			if (fg('platform_native_embeds_rollout_non_maui_experience')) {
				return {
					type: 'extension',
					attrs: {
						extensionKey: `${NATIVE_EMBED_EXTENSION_KEY}:database`,
						extensionType: NATIVE_EMBED_EXTENSION_TYPE,
						parameters: {
							macroParams: {
								url: { value: result },
								alwaysShowTitle: { value: 'true' },
							},
						},
					},
				};
			}

			return {
				type: 'embedCard',
				attrs: {
					url: result,
				},
			};
		} else {
			return;
		}
	};
};

const getDatabaseIcon = () => {
	return <DatabaseIcon label="" />;
};

const getQuickInsertItemsForPage = ({
	pageId,
	spaceKey,
	spaceId,
	experienceTracker,
	createAnalyticsEvent,
	intl,
	flags,
	pageTreeStateUpdater,
}: {
	pageId: string;
	spaceKey: string;
	spaceId: string;
	experienceTracker?: ExperienceTrackerAPI;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	flags?: FlagsStateContainer;
	intl: IntlShape;
	pageTreeStateUpdater?: PageTreeStateUpdater;
}) => {
	const items: ExtensionModule[] = [
		{
			key: 'create-database',
			title: intl.formatMessage(i18n.databaseDefaultTitle),
			description: intl.formatMessage(i18n.databaseDefaultDescription),
			preview: {
				image: {
					dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVK2.png',
					light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVM0.png',
				},
				attribution: {
					name: intl.formatMessage(quickInsertMessages.previewAttributionConfluence),
					icon: ConfluenceAttributionIcon,
				},
			},
			...(isExperimentEnabled('platform_editor_slash_command')
				? { category: 'create', priority: CREATE_SECTION_RANK[DATABASE_MENU_ITEM.key] }
				: { priority: -100 }),
			icon: async () => () => getDatabaseIcon(),
			action: createDatabaseQuickInsertModule({
				pageId,
				spaceKey,
				spaceId,
				experienceTracker,
				createAnalyticsEvent,
				flags,
				pageTreeStateUpdater,
			}),
			featured: true,
		},
	];

	return items;
};

export const createDatabaseExtension = ({
	pageId,
	spaceKey,
	spaceId,
	intl,
	flags,
	experienceTracker,
	createAnalyticsEvent,
	contentType,
	allowDatabaseQuickInsert,
	isEdit,
	pageTreeStateUpdater,
}: CreateDatabaseExtensionProps): ExtensionManifest => {
	const quickInsert: ExtensionModule[] = [];

	if (contentType === 'page') {
		quickInsert.push(
			...getQuickInsertItemsForPage({
				pageId,
				spaceKey,
				spaceId,
				experienceTracker,
				createAnalyticsEvent,
				intl,
				flags,
				pageTreeStateUpdater,
			}),
		);
	}

	quickInsert.push({
		key: 'reference-database',
		title: intl.formatMessage(i18n.referenceDatabaseTitle),
		description: intl.formatMessage(i18n.referenceDatabaseDescription),
		preview: {
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVHP.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLI.png',
			},
			attribution: {
				name: intl.formatMessage(quickInsertMessages.previewAttributionConfluence),
				icon: ConfluenceAttributionIcon,
			},
		},
		...(isExperimentEnabled('platform_editor_slash_command')
			? { category: 'data-and-charts', priority: -1300 }
			: {}),
		icon: async () => () =>
			isExperimentEnabled('platform_editor_slash_command') ? (
				<SmartLinkEmbedIcon label="" />
			) : (
				<img src={InlineEmbedIcon} alt={intl.formatMessage(i18n.referenceDatabaseIconAltText)} />
			),
		action: referenceDatabaseQuickInsertModule(intl),
	});

	return {
		title: intl.formatMessage(i18n.manifestTitle),
		type: 'com.atlassian.confluence.macro.core',
		key: 'database-extension',
		icons: {
			'48': async () => () => getDatabaseIcon(),
		},
		modules: {
			quickInsert: allowDatabaseQuickInsert ? quickInsert : [],
			nodes: {
				default: {
					type: 'inlineExtension',
					render: async () => {
						const sessionData = await getSessionData();
						return ({ node }) => {
							const contentId = node.parameters?.macroParams?.contentId.value;
							const entryId = node.parameters?.macroParams?.entryId.value;
							const fieldId = node.parameters?.macroParams?.fieldId.value;
							return (
								<SessionDataContext.Provider value={sessionData}>
									<DatabaseValueContainer
										contentId={contentId}
										fieldId={fieldId}
										entryId={entryId}
										isEdit={isEdit}
									/>
								</SessionDataContext.Provider>
							);
						};
					},
				},
			},
		},
	};
};
